import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { aggregateStats, cumulativePoints } from '@mahjong/core';
import {
  AppBar,
  Body,
  Card,
  Chip,
  EmptyState,
  ErrorBox,
  Label,
  ListCard,
  Screen,
  Spinner,
  cx,
} from '../components/ui';
import { PeriodFilter, inPeriod, periodLabel } from '../components/PeriodFilter';
import type { PeriodRange } from '../components/PeriodFilter';
import { useGroup, useGroupToken, useMatches } from '../hooks/useGroup';
import { formatAverage, formatPoint, formatScore } from '../lib/format';

/** 順位ごとの色。画面案9の積み上げバーに合わせる */
const RANK_COLORS = ['#B8862A', '#1E7A55', '#5E7F6B', '#B32B24'];

/**
 * 個人成績（画面案9 / §4.5）。
 *
 * 累計ポイントと推移を1枚に収め、そこから細かい指標へ降りていく順序。
 * 順位分布は数値の表より、幅で偏りが見える積み上げバーのほうが速く読める。
 */
export function StatsPage() {
  const { groupId, memberId } = useParams<{ groupId: string; memberId: string }>();
  const token = useGroupToken(groupId);
  const group = useGroup(groupId, token);
  const matches = useMatches(groupId, token, 200);
  const [period, setPeriod] = useState<PeriodRange>({ period: 'all' });

  if (!token) {
    return (
      <Screen>
        <AppBar title="成績" backTo="/" />
        <Body>
          <ErrorBox message="この端末に参加情報がありません。共有URLから開いてください。" />
        </Body>
      </Screen>
    );
  }

  if (group.isLoading || matches.isLoading) {
    return (
      <Screen>
        <AppBar title="成績" backTo={`/g/${groupId}`} />
        <Body>
          <Spinner />
        </Body>
      </Screen>
    );
  }

  const all = matches.data?.matches ?? [];
  const filtered = all.filter((m) => inPeriod(m.playedAt, period));

  const stats = aggregateStats(filtered);
  const me = stats.find((s) => s.memberId === memberId);
  const memberName =
    group.data?.members.find((m) => m.memberId === memberId)?.name ?? me?.nameSnapshot ?? 'メンバー';

  const series = memberId ? cumulativePoints(filtered, memberId) : [];

  return (
    <Screen>
      <AppBar
        title={memberName}
        subtitle={`${periodLabel(period)} · ${me?.matchCount ?? 0}戦`}
        backTo={`/g/${groupId}`}
      />

      <Body className="gap-2.5">
        <PeriodFilter value={period} onChange={setPeriod} />

        {!me ? (
          <EmptyState
            title="この期間の対局がありません"
            description="期間を変えると過去の成績が見られます。"
          />
        ) : (
          <>
            {/* --- 累計ポイントと推移 --- */}
            <Card>
              <Label>累計ポイント</Label>
              <div className="mt-1 flex items-end gap-2.5">
                <span
                  className={cx(
                    'text-[44px] font-black leading-none tabular-nums tracking-tight',
                    me.totalPoint >= 0 ? 'pt-plus' : 'pt-minus',
                  )}
                >
                  {formatPoint(me.totalPoint)}
                </span>
                <span className="pb-1.5 text-xs text-ink-faint">pt</span>
              </div>
              <TrendChart series={series.map((s) => s.cumulative)} />
            </Card>

            {/* --- 主要指標 --- */}
            <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl bg-line">
              <Kpi label="平均順位" value={formatAverage(me.averageRank)} />
              <Kpi label="トップ率" value={`${Math.round(me.topRate * 100)}%`} />
              <Kpi label="ラス率" value={`${Math.round(me.lastRate * 100)}%`} />
            </div>

            {/* --- 順位分布 --- */}
            <Label className="mt-1">順位の分布</Label>
            <div className="flex h-6 overflow-hidden rounded-md text-[10px] text-white">
              {me.rankCounts.map((count, i) =>
                count > 0 ? (
                  <div
                    key={i}
                    style={{ flex: count, background: RANK_COLORS[i] ?? '#5E7F6B' }}
                    className="grid place-items-center font-bold"
                    title={`${i + 1}位 ${count}回`}
                  >
                    {count}
                  </div>
                ) : null,
              )}
            </div>
            <div className="flex gap-3 text-[10.5px] text-ink-faint">
              {me.rankCounts.map((count, i) => (
                <span key={i} className="flex items-center gap-1">
                  <span
                    className="inline-block size-2 rounded-sm"
                    style={{ background: RANK_COLORS[i] ?? '#5E7F6B' }}
                  />
                  {i + 1}位 {count}回
                </span>
              ))}
            </div>

            {/* --- 細かい指標 --- */}
            <ListCard className="mt-1 py-1">
              <StatRow label="連対率" value={`${Math.round(me.quinellaRate * 100)}%`} />
              <StatRow label="平均素点" value={formatScore(Math.round(me.averageRawScore))} />
              <StatRow
                label="最高ポイント"
                value={formatPoint(me.bestPoint)}
                tone={me.bestPoint >= 0 ? 'plus' : 'minus'}
              />
              <StatRow
                label="最低ポイント"
                value={formatPoint(me.worstPoint)}
                tone={me.worstPoint >= 0 ? 'plus' : 'minus'}
              />
              <StatRow label="飛んだ回数" value={`${me.bankruptCount}`} />
              <StatRow label="飛ばした回数" value={`${me.bustCount}`} />
            </ListCard>
          </>
        )}

        {/* --- 他のメンバーへ --- */}
        {stats.length > 1 && (
          <>
            <Label className="mt-1">ほかのメンバー</Label>
            <div className="flex flex-wrap gap-1.5">
              {stats
                .filter((s) => s.memberId !== memberId)
                .map((s) => (
                  <Link key={s.memberId} to={`/g/${groupId}/stats/${s.memberId}`}>
                    <Chip>
                      {s.nameSnapshot} {formatPoint(s.totalPoint)}
                    </Chip>
                  </Link>
                ))}
            </div>
          </>
        )}
      </Body>
    </Screen>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white px-2 py-3 text-center">
      <div className="text-[17px] font-black tabular-nums">{value}</div>
      <div className="mt-0.5 text-[10.5px] text-ink-faint">{label}</div>
    </div>
  );
}

function StatRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'plus' | 'minus';
}) {
  return (
    <div className="flex items-center gap-3 border-t border-line py-3 first:border-t-0">
      <span className="text-[13.5px]">{label}</span>
      <span
        className={cx(
          'ml-auto text-sm font-bold tabular-nums',
          tone === 'plus' && 'pt-plus',
          tone === 'minus' && 'pt-minus',
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * 累計ポイントの推移（§4.5）。
 * 依存を増やさずに済む規模なので、素の SVG で描く。
 */
function TrendChart({ series }: { series: number[] }) {
  if (series.length === 0) return null;

  const W = 300;
  const H = 76;
  const PAD = 8;

  const min = Math.min(0, ...series);
  const max = Math.max(0, ...series);
  const range = max - min || 1;

  const x = (i: number) => (series.length === 1 ? W / 2 : (i / (series.length - 1)) * W);
  const y = (v: number) => H - PAD - ((v - min) / range) * (H - PAD * 2);

  const points = series.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const zeroY = y(0);
  const last = series[series.length - 1]!;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="mt-3 h-[76px] w-full"
      role="img"
      aria-label={`累計ポイントの推移。最新 ${formatPoint(last)}`}
    >
      {/* 0 のライン */}
      <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="#DDD6C4" strokeWidth="1" />
      {series.length > 1 && (
        <polyline
          points={points}
          fill="none"
          stroke="#123528"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      <circle cx={x(series.length - 1)} cy={y(last)} r="4" fill="#123528" />
    </svg>
  );
}
